<template>
  <div class="modal-card">
    <CloseIcon :size="24" class="close-modal-icon close-on-click" />
    <div class="modal-card-header">
      <h1 class="title">Add TRT Patient</h1>
      <p class="subtitle">Enroll an existing patient into the TRT program.</p>
    </div>

    <div class="modal-card-body">
      <div class="field">
        <label class="form-label label-required">Patient</label>
        <PatientSelectInput v-model="patientId" />
      </div>

      <div v-if="needsEmr" class="emr-warning">
        <AlertIcon :size="18" class="emr-warning-icon" />
        <span>
          This patient isn't linked to an EMR record yet. You'll need to link them
          to set up info syncing before they can be added.
        </span>
      </div>
    </div>

    <div class="modal-card-footer">
      <button class="button primary form-button" :disabled="!canSubmit" @click="handleSubmit">
        {{ buttonLabel }}
      </button>
    </div>
  </div>
</template>

<script>
import CloseIcon from 'vue-material-design-icons/Close.vue';
import AlertIcon from 'vue-material-design-icons/AlertOutline.vue';
import PatientSelectInput from '@/components/inputs/PatientSelectInput.vue';
import * as api from '@/api';
import { queryClient } from '@/libs/queryClient';

export default {
  name: 'AddTRTPatientModal',
  components: { CloseIcon, AlertIcon, PatientSelectInput },
  emits: ['add', 'continue'],
  data() {
    return {
      patientId: null,
      selectedPatient: null,
      loadingPatient: false
    };
  },
  computed: {
    // A patient with no EMR integration id (ehrId) must connect one first.
    needsEmr() {
      return !!this.selectedPatient && !this.selectedPatient.ehrId;
    },
    buttonLabel() {
      return this.needsEmr ? 'Continue' : 'Add Patient';
    },
    canSubmit() {
      return !!this.selectedPatient && !this.loadingPatient;
    }
  },
  watch: {
    patientId: {
      handler(newVal) {
        this.loadSelectedPatient(newVal);
      }
    }
  },
  methods: {
    async loadSelectedPatient(patientId) {
      if (!patientId) {
        this.selectedPatient = null;
        return;
      }
      this.loadingPatient = true;
      try {
        this.selectedPatient = await queryClient.fetchQuery({
          queryKey: ['patient', patientId],
          queryFn: () => api.fetchPatientById(patientId)
        });
      } catch (err) {
        console.error('Failed to load selected patient:', err);
        this.selectedPatient = null;
      } finally {
        this.loadingPatient = false;
      }
    },
    handleSubmit() {
      if (!this.canSubmit) return;
      // Needs an EMR link first → move to the EMR search step.
      // Already connected → enroll straight away.
      this.$emit(this.needsEmr ? 'continue' : 'add', this.selectedPatient);
    }
  }
};
</script>

<style scoped>
.subtitle {
  font-size: 14px;
  color: #6b7280;
  margin-top: 8px;
}

.modal-card-body {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.emr-warning {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.4;
  color: #92400e;
  background-color: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: var(--border-radius);
}

.emr-warning-icon {
  flex-shrink: 0;
  color: #d97706;
}
</style>
